'use client'

import React from 'react'
import { Button } from '@repo/ui/button'
import { useRouter } from 'next/navigation'
import { Organization as IdentityOrganization } from '@repo/api/identity-service'
import { serializeFiltersToUrl } from '@/lib/url-filters'
import { OrganizationHeader } from './OrganizationHeader'
import OrganizationProfile from './OrganizationProfile'
import SpecimensChart from './SpecimensChart'
import { useIsMobile } from '@repo/ui/use-mobile'
import { useAuthContext } from '@/contexts/AuthContext'

export type Organization = IdentityOrganization & { id: string }

const CONTAINER_CLASSES = 'w-full sm:max-w-[500px] lg:max-w-[1100px]'
const FOOTER_CLASSES = 'sticky bottom-0 py-6 px-6 bg-white border-t-[1px] border-grey-100'

const generateInventoryLink = (organizationId: string) => {
  const params = new URLSearchParams()
  // Use the correct filter format that matches the specimen center
  const filter = {
    key: 'organization_id',
    operator: 'equals' as const,
    value: organizationId,
    negated: false,
  }
  const serializedFilters = serializeFiltersToUrl([filter])
  if (serializedFilters) params.set('filters', serializedFilters)

  return params.toString()
}

export default function OrganizationPage({ ...org }: Organization) {
  const { isAuthenticated } = useAuthContext()
  const router = useRouter()
  const isMobile = useIsMobile()

  const inventoryParams = React.useMemo(() => generateInventoryLink(org.id), [org.id])

  const handleViewInventory = () => {
    if (isMobile) return router.push('/mobile-email')

    if (isAuthenticated) return router.push(`/?${inventoryParams}`)

    // TODO: persist inventory with cookie
    return router.push('/login')
  }

  return (
    <div className="flex flex-col min-h-screen">
      <OrganizationHeader {...org} isOrgPage />

      <div className="flex-1 px-6 py-4">
        <div className={`${CONTAINER_CLASSES} mx-auto space-y-8`}>
          <OrganizationProfile {...org} />
          {org.id && <SpecimensChart organizationId={org.id} />}
        </div>
      </div>

      <footer className={FOOTER_CLASSES}>
        <div className={`flex items-center justify-center sm:justify-end sm:mx-auto ${CONTAINER_CLASSES}`}>
          <Button onClick={handleViewInventory} className="w-full sm:max-w-[260px]">
            {isMobile
              ? 'View inventory on desktop'
              : isAuthenticated
                ? 'View full inventory'
                : 'Sign In to view full inventory'}
          </Button>
        </div>
      </footer>
    </div>
  )
}
